import { TimeRangeOption } from '../../../types/reservation-form';
import { put } from 'typed-redux-saga';
import { actions } from '../../ducks/reservation-form-slice';
import { EVisitItem, IEVisitEvent, IEVisitEvents } from '../../../types/e-visits';
import { format, getHours, getMinutes, parseISO } from 'date-fns';
import { TPositionedEvent } from '../../../components/ui/calendar/calendar-week/week-utils';
import { ICalendarEvent } from '../../../components/ui/calendar/calendars';
import moment from 'moment';
import { getDateFormat, getTimeFormat } from '@hilbi/web-common/utils/common';
import {
  IPatientSelectOption,
  ITagSelectOption,
} from '@hilbi/web-common/components/e-visits/select/options/types';

export function getTagOptionsFromResponse(response: any): ITagSelectOption[] {
  return response.data.data.tags.map((tag: any) => {
    return {
      label: tag.name,
      value: tag.id,
      color: tag.color,
    };
  });
}

export function getPatientOptionsFromResponse(response: any): IPatientSelectOption[] {
  return response.data.data.items.map((receivedPatient: any) => {
    return {
      label: receivedPatient.profile.firstName + ' ' + receivedPatient.profile.lastName,
      email: receivedPatient.email,
      value: receivedPatient.id,
      image: receivedPatient.profile?.profileImage || '',
    };
  });
}

const RESERVATION_FORM_ERROR_CODES = new Map([
  ['event_conflict', 'reservation_form_event_conflict'],
]);

export function* handleApiError({ error }: { error: any }) {
  console.error(error);

  const code = error?.data?.errCode || '';
  let translated = false;
  let message = '';

  if (code === 'validation_error') {
    message = error.data.errMessage;
  } else if (RESERVATION_FORM_ERROR_CODES.has(code)) {
    translated = true;
    message = RESERVATION_FORM_ERROR_CODES.get(code) as string;
  } else {
    translated = true;
    message = 'reservation_form_unknown_error';
  }

  yield* put(
    actions.setErrorMessage({
      message,
      translated,
    }),
  );
}

function optionLabelFormatter(event: IEVisitEvent) {
  return `${format(parseISO(event.from), 'HH:mm')} - ${format(parseISO(event.to), 'HH:mm')}`;
}

export function getTimeRangeOptions(
  events: IEVisitEvents,
  editedItem: IEVisitEvent,
): TimeRangeOption[] {
  const allEventsForDay = events[Object.keys(events)[0]];
  const result: TimeRangeOption[] = [
    {
      value: editedItem.eventId || 'edited_event_id',
      label: optionLabelFormatter(editedItem),
      default: true,
      from: editedItem.from,
      to: editedItem.to,
    },
  ];

  if (allEventsForDay) {
    for (const eVisitItem of allEventsForDay) {
      if (!eVisitItem.order) {
        result.push({
          value: eVisitItem.event.eventId,
          label: optionLabelFormatter(eVisitItem.event),
          from: eVisitItem.event.from,
          to: eVisitItem.event.to,
        });
      }
    }
  }

  return result;
}

export function getEditedEvent(positions: TPositionedEvent<ICalendarEvent<EVisitItem>>[]) {
  return positions.find((positionedEvent) => {
    return positionedEvent.event.eventData?.event.editing;
  }) as TPositionedEvent<ICalendarEvent<EVisitItem>>;
}

export function getValuesFromDateField(
  dateField: string,
  positions: TPositionedEvent<ICalendarEvent<EVisitItem>>[],
) {
  const editedEvent = getEditedEvent(positions);
  if (!editedEvent) {
    throw new Error('Edited event not found');
  }

  const dateFieldDate = parseISO(dateField);
  const dates = {
    year: dateFieldDate.getFullYear(),
    month: dateFieldDate.getMonth(),
    day: dateFieldDate.getDate(),
  };

  return {
    to: (() => {
      const hourDate = new Date(editedEvent.event.eventData?.event.to || new Date());
      return new Date(dates.year, dates.month, dates.day, getHours(hourDate), getMinutes(hourDate));
    })(),
    from: (() => {
      const hourDate = new Date(editedEvent.event.eventData?.event.from || new Date());
      return new Date(dates.year, dates.month, dates.day, getHours(hourDate), getMinutes(hourDate));
    })(),
  };
}

export function getDisplayedDates(event: ICalendarEvent<any>) {
  const fromObj = moment(event.start);
  const fromDate = fromObj.format(getDateFormat());
  const fromTime = fromObj.format(getTimeFormat(false));
  const toTime = moment(event.end).format(getTimeFormat(false));

  return {
    displayedDateTime: `${fromDate} ${fromTime} - ${toTime}`,
    displayedDate: `${fromDate}`,
  };
}
